import React, { useMemo } from "react"

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { DateTime } from "luxon"

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table.tsx"
import type { DataPointValue } from "@/types/dataPointTypes.ts"

const columnHelper = createColumnHelper<DataPointValue>()

export default function DataPointValueTable({ data }: { data: DataPointValue[] }) {
  const columnDefs = useMemo(
    () => [
      columnHelper.accessor((v) => v.timestamp, {
        id: "timestamp",
        header: () => <TableHead className="p-4 w-[200px]">Timestamp</TableHead>,
        cell: (context) => (
          <TableCell className="p-4">
            {DateTime.fromISO(context.getValue()).toFormat("D ttt")}
          </TableCell>
        ),
      }),
      columnHelper.accessor((v) => v.value, {
        id: "value",
        header: () => <TableHead className="p-4 text-center">Value</TableHead>,
        cell: (context) => (
          <TableCell className="p-4 text-center">
            {context.getValue() != null ? context.getValue()?.toLocaleString() : "N/A"}
          </TableCell>
        ),
      }),
    ],
    [],
  )

  const table = useReactTable<DataPointValue>({
    data: data || [],
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <Table className="grow-1">
      {table.getHeaderGroups().map((hg) => (
        <TableHeader className="bg-accent border-b sticky top-0" key={hg.id}>
          <TableRow>
            {hg.headers.map((header, i) => (
              <React.Fragment key={i}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </React.Fragment>
            ))}
          </TableRow>
        </TableHeader>
      ))}
      <TableBody>
        {table.getRowModel().rows.map((row) => (
          <TableRow key={row.id}>
            {row.getVisibleCells().map((cell, i) => (
              <React.Fragment key={i}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </React.Fragment>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
